// Watches XR8's fresh-camera-frame signal while the AR view runs. On the
// Galaxy A14 the camera background has been seen to freeze once an overlay
// video starts, while the video keeps rendering (ARWEB-42). If fresh frames
// stop for longer than the threshold, the guard records what the camera
// element and track look like and resumes the camera element when the
// browser has paused it. Everything it observes is kept for diagnostics.

export const STALL_THRESHOLD_MS = 1500;
export const RETRY_INTERVAL_MS = 3000;
// A longer gap between update callbacks means the loop itself paused
// (background tab, engine pause), which is not a camera stall.
export const UPDATE_GAP_MS = 1000;
const MAX_EVENTS = 20;

export function createCameraStallGuard({now, cameraVideo, cameraTrack, isHidden = () => false, clock = () => new Date().toISOString()}) {
  let lastUpdateAt = null;
  let lastFreshAt = null;
  let stalledSince = null;
  let lastAttemptAt = -Infinity;
  const events = [];

  function record(kind, detail) {
    events.push({kind, at: clock(), ...detail});
    if (events.length > MAX_EVENTS) events.shift();
  }

  function sample() {
    const video = safeCall(cameraVideo);
    const track = safeCall(cameraTrack);
    return {
      cameraVideo: video ? {
        paused: Boolean(video.paused),
        ended: Boolean(video.ended),
        readyState: Number.isInteger(video.readyState) ? video.readyState : null,
        currentTimeSeconds: Number.isFinite(video.currentTime) ? Math.round(video.currentTime * 1000) / 1000 : null,
      } : null,
      cameraTrack: track ? {
        enabled: typeof track.enabled === 'boolean' ? track.enabled : null,
        muted: typeof track.muted === 'boolean' ? track.muted : null,
        readyState: ['live', 'ended'].includes(track.readyState) ? track.readyState : null,
      } : null,
    };
  }

  return {
    update(frameStartResult, tracking) {
      const t = now();
      const resumedAfterGap = lastUpdateAt === null || t - lastUpdateAt > UPDATE_GAP_MS;
      lastUpdateAt = t;
      if (frameStartResult?.repeatFrame === false) {
        if (stalledSince !== null) record('recovered', {stalledMs: Math.round(t - stalledSince), tracking: Boolean(tracking)});
        lastFreshAt = t;
        stalledSince = null;
        return;
      }
      if (frameStartResult?.repeatFrame !== true) return;
      if (resumedAfterGap || lastFreshAt === null) {
        lastFreshAt = t;
        return;
      }
      if (isHidden() || t - lastFreshAt < STALL_THRESHOLD_MS) return;
      if (stalledSince === null) {
        stalledSince = lastFreshAt;
        record('stalled', {tracking: Boolean(tracking), ...sample()});
      }
      if (t - lastAttemptAt < RETRY_INTERVAL_MS) return;
      lastAttemptAt = t;
      const video = safeCall(cameraVideo);
      if (video && video.paused && !video.ended) {
        record('resume-camera-video', sample());
        try {
          Promise.resolve(video.play()).catch(() => {});
        } catch {
          // A rejected or throwing play() is recorded by the next sample.
        }
      }
    },
    reset() {
      lastUpdateAt = null;
      lastFreshAt = null;
      stalledSince = null;
      lastAttemptAt = -Infinity;
    },
    get events() {
      return events.map(event => ({...event}));
    },
  };
}

function safeCall(get) {
  try {
    return get?.() ?? null;
  } catch {
    return null;
  }
}
